import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import "./LoginScreen.css";
import "../../CSS/fonts.css";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../contexts/GlobalContext";

const LOGIN_URL = "/api/login";

const parseLoginResponseJson = async (response) => {
  try {
    const text = await response.text();
    if (!text) {
      return {};
    }
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const extractAccessToken = (data = {}) => data.accessToken ?? data.AccessToken ?? null;
const extractRefreshToken = (data = {}) => data.refreshToken ?? data.RefreshToken ?? null;
const extractExpiresIn = (data = {}) => data.expiresIn ?? data.ExpiresIn;

/** 401 Problem Details: MFA required (second factor needed). */
const isTwoFactorChallengeUnauthorized = (data = {}) => {
  const detail = data.detail ?? data.Detail;
  return detail === "RequiresTwoFactor";
};

const SignIn = () => {
  let devUserName = "";
  let devPassword = "";

  if (process.env.NODE_ENV === "development") {
    devUserName = process.env.REACT_APP_DEV_USER_NAME;
    devPassword = process.env.REACT_APP_DEV_USER_PASSWORD;
  } else {
    devUserName = "";
    devPassword = "";
  }

  const [username, setUsername] = useState(devUserName);
  const [password, setPassword] = useState(devPassword);
  const [mfaCode, setMfaCode] = useState("");
  const [showMfaFields, setShowMfaFields] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const { fetchUserData, isAuthorized } = useContext(GlobalContext);

  useEffect(() => {
    if (isWaitingForAuth && isAuthorized) {
      setIsWaitingForAuth(false);
      navigate("/", { replace: true });
    }
  }, [isWaitingForAuth, isAuthorized, navigate]);

  const handleBackToCredentials = () => {
    if (loading) {
      return;
    }
    setShowMfaFields(false);
    setMfaCode("");
    setLoginError("");
    setPassword("");
  };

  const handleLogin = async () => {
    setLoginError("");
    setLoading(true);
    setIsWaitingForAuth(false);

    if (showMfaFields && !mfaCode.trim()) {
      setLoginError(t("Enter 6 digit code"));
      setLoading(false);
      return;
    }

    try {
      const body = {
        email: username.trim(),
        password,
      };

      if (showMfaFields && mfaCode.trim()) {
        body.twoFactorCode = mfaCode.trim();
      }

      const response = await fetch(LOGIN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await parseLoginResponseJson(response);

      if (response.ok) {
        const accessToken = extractAccessToken(data);
        const refreshToken = extractRefreshToken(data);
        const expiresIn = extractExpiresIn(data);

        if (accessToken && refreshToken != null && expiresIn != null) {
          await login(accessToken, refreshToken, expiresIn);
          setShowMfaFields(false);
          setMfaCode("");
          setIsWaitingForAuth(true);

          try {
            await fetchUserData();
          } catch (error) {
            console.error("Error fetching user data:", error);
            setLoginError(t("Login successful but failed to load user data. Please try again."));
            setIsWaitingForAuth(false);
          }
          return;
        }

        setLoginError(t("Failed to login"));
        return;
      }

      const isTwoFactorChallenge = response.status === 401 && isTwoFactorChallengeUnauthorized(data);

      if (isTwoFactorChallenge && !showMfaFields) {
        setShowMfaFields(true);
        setLoginError("");
        setLoading(false);
        return;
      }

      if (!isTwoFactorChallenge) {
        setShowMfaFields(false);
        setMfaCode("");
      }

      let errorMessage = t("Failed to login");

      if (response.status === 401) {
        const sentTwoFactor = showMfaFields && Boolean(mfaCode.trim());
        if (sentTwoFactor) {
          errorMessage = t("Invalid MFA code");
        } else {
          errorMessage = t("Invalid username or password");
        }
      } else if (response.status === 403) {
        errorMessage = t("Account is locked or disabled");
      } else if (response.status === 429) {
        errorMessage = t("Too many login attempts. Please try again later");
      } else if (response.status >= 500) {
        errorMessage = t("Server error. Please try again later");
      }

      setLoginError(errorMessage);
    } catch (error) {
      setLoginError(t("Failed to login"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading && !isWaitingForAuth) {
      handleLogin();
    }
  };

  const handleMfaDialogClose = (_event, reason) => {
    if (reason === "backdropClick" && loading) {
      return;
    }
    handleBackToCredentials();
  };

  const handleMfaKeyDown = (event) => {
    if (event.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  const isFormDisabled = loading || isWaitingForAuth;

  return (
    <div className="split-screen">
      <div className="left-pane">
        <div className="content">
          <img src="/static/img/delta-customer-portal-blue.png" alt={t("Delta Customer Portal")} className="login-logo" />
          {!showMfaFields && (
            <div className="main-text-container">
              <p className="login-instruction" style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}>
                {t("Welcome to the Delta Customer Portal! Please log in.")}
              </p>
            </div>
          )}
          <div
            className="button-container"
            style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
          >
            {!showMfaFields && loginError && (
              <div
                role="alert"
                style={{
                  fontFamily: "'Helvetica Neue Light', sans-serif",
                  fontSize: "1rem",
                  marginBottom: "1rem",
                  alignSelf: "flex-start",
                  color: "red",
                }}
              >
                {loginError}
              </div>
            )}
            {!showMfaFields && isFormDisabled && (
              <div
                style={{
                  fontFamily: "'Helvetica Neue Light', sans-serif",
                  fontSize: "1rem",
                  marginBottom: "1rem",
                  alignSelf: "flex-start",
                  color: "blue",
                }}
              >
                {isWaitingForAuth ? t("Authorizing...") : t("Signing in...")}
              </div>
            )}
            {!showMfaFields && (
              <>
                <TextField
                  label={t("Username")}
                  variant="outlined"
                  fullWidth
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ marginBottom: "1rem" }}
                  disabled={isFormDisabled}
                />
                <TextField
                  label={t("Password")}
                  type="password"
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ marginBottom: "10px" }}
                  disabled={isFormDisabled}
                />
                <a
                  href="https://deltarefundsolutions.com/Contact"
                  className="forgot-password"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontFamily: "'Helvetica Neue Light', sans-serif", marginBottom: "40px", alignSelf: "flex-start" }}
                >
                  {t("Forgot password?")}
                </a>
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  <Button className="login-button" onClick={handleLogin} variant="contained" disabled={isFormDisabled}>
                    {isFormDisabled ? <CircularProgress size={24} color="inherit" /> : t("Sign in")}
                  </Button>
                </div>
              </>
            )}
          </div>

          {!showMfaFields && (
            <div className="extra-links extra-link-container">
              <a
                href="https://deltarefundsolutions.com/Contact"
                className="extra-link"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}
              >
                {t("Already a customer, but can't log in?")}
              </a>
              <a
                href="https://deltarefundsolutions.com/Contact"
                className="extra-link"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: "'Helvetica Neue Light', sans-serif" }}
              >
                {t("Want to become a customer?")}
              </a>
            </div>
          )}
        </div>
      </div>
      <div className="right-pane">{/* Background image set via CSS */}</div>

      <Dialog
        open={showMfaFields}
        onClose={handleMfaDialogClose}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={loading}
        aria-labelledby="mfa-dialog-title"
        aria-describedby="mfa-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            px: { xs: 1, sm: 2 },
            py: 1,
          },
        }}
      >
        <DialogTitle
          id="mfa-dialog-title"
          component="h2"
          sx={{
            fontFamily: "'Helvetica Neue', 'Helvetica Neue Light', Helvetica, Arial, sans-serif",
            fontWeight: 600,
            fontSize: "1.35rem",
            color: "#333",
            pb: 0.5,
          }}
        >
          {t("Verify your identity")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            id="mfa-dialog-description"
            variant="body2"
            sx={{
              color: "#6b6a6a",
              mb: 2,
              fontFamily: "'Helvetica Neue Light', sans-serif",
              lineHeight: 1.5,
            }}
          >
            {t("MFA login finish description")}
          </Typography>
          {loginError && (
            <Typography
              role="alert"
              sx={{
                color: "error.main",
                fontSize: "0.95rem",
                mb: 2,
                fontFamily: "'Helvetica Neue Light', sans-serif",
              }}
            >
              {loginError}
            </Typography>
          )}
          <TextField
            label={t("MFA code")}
            variant="outlined"
            fullWidth
            autoFocus
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            onKeyDown={handleMfaKeyDown}
            disabled={loading}
            inputProps={{
              inputMode: "numeric",
              autoComplete: "one-time-code",
              maxLength: 12,
              "aria-label": t("MFA code"),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, px: 3, pb: 2, gap: 1 }}>
          <Button
            type="button"
            variant="text"
            onClick={handleBackToCredentials}
            disabled={loading}
            aria-label={t("Back to sign in")}
            sx={{ textTransform: "none", fontFamily: "'Helvetica Neue Light', sans-serif" }}
          >
            {t("Back to sign in")}
          </Button>
          <Button
            type="button"
            className="login-button"
            onClick={handleLogin}
            variant="contained"
            disabled={loading}
            aria-label={t("Continue")}
            sx={{ minWidth: 160 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t("Continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SignIn;
